import React from 'react';
import { TimkenSpringRatesPreload, TimkenUnit } from '../../components/bearing-search/types/types';
import { handleActionError } from '../utils/engineering-tools';
import { convertNumberInput } from '../utils/validations';
import { bearingTypes } from '../../components/bearing-search/api/api';

export type BearingSpringRatesState = {
	bearingClass: string;
	partType: Array<string>;
	selectedPartTypes: Array<string>;
	springRatesPreload: TimkenSpringRatesPreload;
	unitSystem: TimkenUnit;
};

export const XAxisTypes = {
	radial: 'radial',
	axial: 'axial'
}

export const YAxisTypes = {
	deflection: 'deflection',
	springRate: 'springRate'
}

/**
 * As bearing spring rates is limited to TRB_TS and TRB_TSF types,
 * Get bearingTypes and set as default value of partType state
 */
const getDefaultPartTypes = () => {
	return bearingTypes.map((item) => item.partType);
};

export const initialSpringRatesState: BearingSpringRatesState = {
	bearingClass: '',
	partType: getDefaultPartTypes(),
	selectedPartTypes: [],
	springRatesPreload: '0',
	unitSystem: 'M',
};
export type BearingSpringRatesAction =
	| { type: 'change_unit'; payload: TimkenUnit }
	| { type: 'change_part_type'; payload: Array<string> }
	| { type: 'change_bearing_class'; payload: string }
	| { type: 'change_spring_rates_preload'; payload: TimkenSpringRatesPreload}
	| { type: 'change_validation_flag'; payload: boolean }
	| { type: 'reset'; payload?: BearingSpringRatesState };

export type BearingSpringRateChartState = {
	axialGraphData?: any;
	radialGraphData?: any;
	xAxisLabel: string;
	yAxisLabel: string;
	graphTitle: string;
	graphType: string;
	yAxisType: string;
	yAxisScaling: boolean;
	xAxisMin?: number;
	xAxisMax?: number;
	yAxisMin?: number;
	yAxisMax?: number;
}
export const initialBearingSpringRateChartState: BearingSpringRateChartState = {
	axialGraphData: null,
	radialGraphData: null,
	xAxisLabel: '',
	yAxisLabel: '',
	graphTitle: '',
	graphType: 'radial',
	yAxisType: 'deflection',
	yAxisScaling: false,
	xAxisMin: undefined,
	xAxisMax: undefined,
	yAxisMin: undefined,
	yAxisMax: undefined,
};
export type BearingSpringRateChartAction =
	| { type: 'set_axial_graph_data'; payload: any }
	| { type: 'set_radial_graph_data'; payload: any }
	| { type: 'set_x_axis_label'; payload: string }
	| { type: 'set_y_axis_label'; payload: string }
	| { type: 'set_graph_title'; payload: string }
	| { type: 'set_graph_type'; payload: string }
	| { type: 'set_y_axis_type'; payload: string }
	| { type: 'set_y_axis_scaling'; payload: boolean }
	| { type: 'set_x_axis_min'; payload: number | undefined }
	| { type: 'set_x_axis_max'; payload: number | undefined }
	| { type: 'set_y_axis_min'; payload: number | undefined }
	| { type: 'set_y_axis_max'; payload: number | undefined }
	| { type: 'change_number_field'; payload: [keyof BearingSpringRateChartState, unknown]; }
	| { type: 'reset'; payload?: BearingSpringRateChartState };

export const bearingSpringRateChartReducer: React.Reducer<
	BearingSpringRateChartState,
	BearingSpringRateChartAction
> = ( state, action ) => {
	switch (action.type) {
		case 'set_axial_graph_data':
			return { ...state, axialGraphData: action.payload };
		case 'set_radial_graph_data':
			return { ...state, radialGraphData: action.payload };
		case 'set_x_axis_label':
			return { ...state, xAxisLabel: action.payload };
		case 'set_y_axis_label':
			return { ...state, yAxisLabel: action.payload };
		case 'set_graph_title':
			return { ...state, graphTitle: action.payload };
		case 'set_graph_type':
			return { ...state, graphType: action.payload };
		case 'set_y_axis_type':
			return { ...state, yAxisType: action.payload };
		case 'set_y_axis_scaling':
			return { ...state, yAxisScaling: action.payload };
		case 'change_number_field': {
			const [ key, value ] = action.payload;

			return {
				...state,
				[ key ]: convertNumberInput( value ),
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialBearingSpringRateChartState;
			return {
				...newState,
			};
		}
		default:
			return state;
	}
};

const springRatesReducer: React.Reducer<
	BearingSpringRatesState,
	BearingSpringRatesAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'change_unit': {
			return {
				...state,
				unitSystem: action.payload,
			};
		}
		case 'change_bearing_class': {
			return {
				...state,
				bearingClass: action.payload,
			};
		}
		case 'change_part_type': {
			return {
				...state,
				partType: (Array.isArray(action.payload) && action.payload.length > 0)
					? action.payload
					: getDefaultPartTypes(),
				selectedPartTypes: action.payload,
			};
		}
		case 'change_spring_rates_preload': {
			return {
				...state,
				springRatesPreload: action.payload,
			};
		}
		case 'change_validation_flag': {
			return {
				...state,
				isValidated: action.payload,
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialSpringRatesState;
			return {
				...newState,
			};
		}
	}
	return handleActionError( action );
};
export default springRatesReducer;
